import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  ConfirmationPopup,
  ContentLoader,
  DeviceIcon,
  IconCheckmark,
  IconDisabled,
  List,
  Notification,
  StatusCard,
} from 'shared/components';
import {
  displayValue,
  formatDate,
  getCurrentUserPermissions,
  hasPermission,
} from 'shared/helpers';
import {
  fetchDevicesByLicenseUser,
  fetchLicensesByLicenseUser,
} from '../../licenses/actions';
import {
  bulkDeviceReset,
  resetDeviceAction,
} from '../../licenses/LicenseContainer/LicenseDevices/actions';
import BulkActionsDropdown from '../../licenses/LicenseContainer/BulkActionsDropdown';

const PAGE_SIZE = 10;
const LICENSES_LOOKUP_SIZE = 50;

const isResettable = (device) => Boolean(get(device, 'device_active'));

const LicenseUserDevices = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const permissionsList = useSelector((state) =>
    getCurrentUserPermissions(state),
  );
  const canResetDevices = hasPermission(permissionsList, 'reset_device');

  const [devices, setDevices] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'last_check' },
  ]);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [licensesById, setLicensesById] = useState({});
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [devicesToReset, setDevicesToReset] = useState([]);
  const [isResetting, setIsResetting] = useState(false);

  const getDevices = useCallback(
    async (page, sort) => {
      setIsLoading(true);
      try {
        const response = await fetchDevicesByLicenseUser(
          licenseUserId,
          sort,
          page,
          PAGE_SIZE,
        );
        setDevices(get(response, 'data.results', []));
        setCount(get(response, 'data.count', 0));
      } catch (error) {
        Notification(
          'error',
          t('Error occurred'),
          t('There was an error while getting devices'),
        );
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [licenseUserId],
  );

  useEffect(() => {
    if (licenseUserId) {
      getDevices(currentPage, currentSort);
    }
  }, [licenseUserId, currentPage, currentSort, getDevices, retryToken]);

  useEffect(() => {
    if (!licenseUserId) {
      return;
    }
    const fetchAllLicenses = async () => {
      const map = {};
      let page = 0;
      let total = 0;
      do {
        const response = await fetchLicensesByLicenseUser(
          licenseUserId,
          [],
          page,
          LICENSES_LOOKUP_SIZE,
        );
        get(response, 'data.results', []).forEach((license) => {
          map[license.id] = license;
        });
        total = get(response, 'data.count', 0);
        page += 1;
      } while (page * LICENSES_LOOKUP_SIZE < total);
      return map;
    };
    fetchAllLicenses()
      .then((map) => setLicensesById(map))
      .catch(() => setLicensesById({}));
  }, [licenseUserId]);

  // A selection only covers the rows on screen, so it is cleared whenever
  // the page or sort changes
  const handlePageChange = (page) => {
    setSelectedDevices([]);
    setCurrentPage(page);
  };

  const handleSortChange = (newSort) => {
    setSelectedDevices([]);
    setCurrentSort(newSort);
  };

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
    setError(false);
  };

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices((selected) =>
      selected.includes(deviceId)
        ? selected.filter((id) => id !== deviceId)
        : selected.concat(deviceId),
    );
  };

  const handleBulkAction = (actionId) => {
    if (actionId === 'reset') {
      setDevicesToReset(selectedDevices);
    }
  };

  const resetDevices = async () => {
    setIsResetting(true);
    try {
      if (devicesToReset.length === 1) {
        await resetDeviceAction(devicesToReset[0]);
      } else {
        await bulkDeviceReset(devicesToReset);
      }
      Notification(
        'success',
        devicesToReset.length > 1
          ? t('Devices successfully reset')
          : t('License device has been reset'),
      );
      setSelectedDevices([]);
      setDevicesToReset([]);
      setRetryToken((token) => token + 1);
    } catch (err) {
      Notification(
        'error',
        t('Your changes were not saved'),
        t('There was an error while saving your changes'),
      );
    } finally {
      setIsResetting(false);
    }
  };

  const hasFloatingLicense = Object.values(licensesById).some(
    (license) =>
      get(license, 'is_floating') || get(license, 'is_floating_cloud'),
  );

  const columns = [
    {
      id: 'select',
      className: 'select-container',
      Cell: (rowData) => {
        const resettable = isResettable(rowData.original);
        return (
          <button
            className="table-select"
            aria-label="Select device"
            disabled={!resettable}
            onClick={(e) => {
              e.stopPropagation();
              if (resettable) {
                toggleDeviceSelection(rowData.original.id);
              }
            }}
            type="button"
          >
            <Checkbox
              inputId={`${rowData.original.id}-select`}
              checked={selectedDevices.includes(rowData.original.id)}
              disabled={!resettable}
            />
          </button>
        );
      },
      width: 60,
      show: canResetDevices,
      sortable: false,
    },
    {
      accessor: 'type',
      Header: t('Type'),
      className: 'text-center',
      headerClassName: 'text-center',
      width: 70,
      Cell: (cellInfo) => (
        <div className="device-type-icon">
          <DeviceIcon device={cellInfo.value} />
        </div>
      ),
    },
    {
      Header: t('Hardware ID'),
      accessor: 'hardware_id',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Product'),
      id: 'license',
      accessor: 'license',
      Cell: (cellData) => {
        const licenseId = cellData.value;
        if (!licenseId) {
          return '-';
        }
        const license = licensesById[licenseId];
        const label =
          get(license, 'product.product_name') ||
          get(license, 'license_key') ||
          t('License');
        return (
          <Link
            to={`/licenses/${licenseId}`}
            onClick={(e) => e.stopPropagation()}
          >
            {label}
          </Link>
        );
      },
      width: 220,
      sortable: false,
    },
    {
      Header: t('First activated'),
      accessor: 'time_activated',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Last checked'),
      accessor: 'last_check',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('In use'),
      accessor: 'floating_in_use',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 80,
      sortable: false,
      show: hasFloatingLicense,
    },
    {
      Header: t('Status'),
      accessor: 'device_active',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellInfo) => (
        <StatusCard
          status={cellInfo.value ? 'success' : 'error'}
          text={cellInfo.value ? t('Active') : t('Inactive')}
        />
      ),
      width: 110,
    },
    {
      id: 'actions',
      Header: t('Actions'),
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => (
        <Button
          size="sm"
          theme="default"
          disabled={!isResettable(rowData.original)}
          onClick={(e) => {
            e.stopPropagation();
            setDevicesToReset([rowData.original.id]);
          }}
        >
          {t('Reset')}
        </Button>
      ),
      width: 100,
      show: canResetDevices,
      sortable: false,
    },
  ];

  if (isLoading) {
    return <ContentLoader text={t('Loading devices')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load devices for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No devices for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      {canResetDevices && (
        <BulkActionsDropdown
          disabled={!selectedDevices.length}
          onActionSelect={handleBulkAction}
          style={{ marginBottom: '1rem' }}
          resetEnabled
        />
      )}
      <List
        data={devices}
        columns={columns}
        minRows={2}
        loading={isLoading}
        manual
        defaultSorted={currentSort}
        onSortedChange={handleSortChange}
        page={currentPage}
        pages={Math.ceil(count / PAGE_SIZE)}
        showPagination={count > PAGE_SIZE}
        onPageChange={handlePageChange}
      />
      {devicesToReset.length > 0 && (
        <ConfirmationPopup
          closeCb={() => setDevicesToReset([])}
          confirmCb={resetDevices}
          title={
            devicesToReset.length > 1
              ? t('Are you sure you want to reset these devices?')
              : t('Are you sure you want to reset this device?')
          }
          confirmText={t('Reset')}
          theme="warning"
          disabled={isResetting}
        />
      )}
    </div>
  );
};

LicenseUserDevices.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserDevices;
